import { TokenActivityItem } from '@/core/tokens';
import { a } from '@react-spring/web';
import {
  Address,
  NFTX_UNISWAP_SUBGRAPH,
  NftxV3Uniswap,
  createQuery,
  getChainConstant,
  querySubgraph,
} from 'nftx.js';
import { parseEther } from 'viem';

const fetchTokenActivity = async ({
  network,
  tokenAddress,
}: {
  network: number;
  tokenAddress: Address;
}) => {
  const q = createQuery<NftxV3Uniswap.Query>();

  const depositsQuery = q.mints
    .first(10)
    .orderBy('timestamp')
    .orderDirection('desc')
    .where((w) => [
      w.amount0.gt('0'),
      w.or(w.token0.is(tokenAddress), w.token1.is(tokenAddress)),
    ])
    .select((s) => [
      s.id,
      s.tickUpper,
      s.tickLower,
      s.amountUSD,
      s.amount0,
      s.amount1,
      s.timestamp,
      s.transaction((t) => [t.id]),
      s.owner((owner) => [owner.id]),
      s.token0((token) => [token.symbol]),
      s.token1((token) => [token.symbol]),
    ]);
  const withdrawsQuery = q.burns
    .first(10)
    .orderBy('timestamp')
    .orderDirection('desc')
    .where((w) => [
      w.amount0.gt('0'),
      w.or(w.token0.is(tokenAddress), w.token1.is(tokenAddress)),
    ])
    .select((s) => [
      s.id,
      s.amountUSD,
      s.amount0,
      s.amount1,
      s.timestamp,
      s.transaction((t) => [t.id]),
      s.owner((owner) => [owner.id]),
      s.token0((token) => [token.symbol]),
      s.token1((token) => [token.symbol]),
    ]);
  const sellsQuery = q.swaps
    .as('sells')
    .first(30)
    .orderBy('timestamp')
    .orderDirection('desc')
    .where((w) => [w.tokenIn.is(tokenAddress)])
    .select((s) => [
      s.id,
      s.transaction((t) => [t.id]),
      s.amount0,
      s.amount1,
      s.amountUSD,
      s.timestamp,
      s.sender((owner) => [owner.id]),
      s.tokenIn((token) => [token.symbol]),
      s.tokenOut((token) => [token.symbol]),
    ]);
  const buysQuery = q.swaps
    .as('buys')
    .first(30)
    .orderBy('timestamp')
    .orderDirection('desc')
    .where((w) => [w.tokenOut.is(tokenAddress)])
    .select((s) => [
      s.id,
      s.transaction((t) => [t.id]),
      s.amount0,
      s.amount1,
      s.amountUSD,
      s.timestamp,
      s.sender((owner) => [owner.id]),
      s.tokenIn((token) => [token.symbol]),
      s.tokenOut((token) => [token.symbol]),
    ]);

  const query = q.combine([
    depositsQuery,
    withdrawsQuery,
    sellsQuery,
    buysQuery,
  ]);

  const data = await querySubgraph({
    url: getChainConstant(NFTX_UNISWAP_SUBGRAPH, network),
    query,
  });

  const getAmounts = (x: { amount0: string; amount1: string }) => {
    let amount0 = parseEther(x.amount0);
    if (amount0 < 0n) {
      amount0 = -amount0;
    }
    let amount1 = parseEther(x.amount1);
    if (amount1 < 0n) {
      amount1 = -amount1;
    }

    return [amount0, amount1] as const;
  };

  const activity: TokenActivityItem[] = [
    ...data.mints.map((x): TokenActivityItem => {
      const [amount0, amount1] = getAmounts(x);

      return {
        type: 'deposit',
        hash: x.transaction.id,
        account: x.owner.id as Address,
        amount: parseEther(x.amountUSD),
        timestamp: Number(x.timestamp),
        token0: {
          amount: amount0,
          symbol: x.token0.symbol,
        },
        token1: {
          amount: amount1,
          symbol: x.token1.symbol,
        },
      };
    }),
    ...data.burns.map((x): TokenActivityItem => {
      const [amount0, amount1] = getAmounts(x);

      return {
        type: 'withdraw',
        hash: x.transaction.id,
        account: x.owner?.id as Address,
        amount: parseEther(x.amountUSD),
        timestamp: Number(x.timestamp),
        token0: {
          amount: amount0,
          symbol: x.token0.symbol,
        },
        token1: {
          amount: amount1,
          symbol: x.token1.symbol,
        },
      };
    }),
    ...data.sells.map((x): TokenActivityItem => {
      const [amount0, amount1] = getAmounts(x);

      return {
        type: 'swap',
        swapType: 'sell',
        hash: x.transaction.id,
        account: x.sender.id as Address,
        amount: parseEther(x.amountUSD),
        timestamp: Number(x.timestamp),
        token0: {
          amount: amount0,
          symbol: x.tokenIn.symbol,
        },
        token1: {
          amount: amount1,
          symbol: x.tokenOut.symbol,
        },
      };
    }),
    ...data.buys.map((x): TokenActivityItem => {
      const [amount0, amount1] = getAmounts(x);

      return {
        type: 'swap',
        swapType: 'buy',
        hash: x.transaction.id,
        account: x.sender.id as Address,
        amount: parseEther(x.amountUSD),
        timestamp: Number(x.timestamp),
        token0: {
          amount: amount0,
          symbol: x.tokenIn.symbol,
        },
        token1: {
          amount: amount1,
          symbol: x.tokenOut.symbol,
        },
      };
    }),
  ].sort((a, b) => {
    return b.timestamp - a.timestamp;
  });

  return activity;
};

export default fetchTokenActivity;
